import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  TextField,
} from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { useEffect, useState } from "react";

import { useRequestCarrier } from "../hooks/useRequestCarrier";

interface RequestCarrierDialogProps {
  carrierName: string;
  onClose: VoidFunction;
}

type DialogState = "idle" | "loading" | "success" | "error";

interface DialogConfig {
  title: string;
  titleColor?: string;
  content: React.ReactNode;
  actions: React.ReactNode;
}

function getDialogConfig(
  state: DialogState,
  name: string,
  handlers: {
    onClose: VoidFunction;
    onSubmit: VoidFunction;
    onRetry: VoidFunction;
    setName: (v: string) => void;
    isContracted: boolean;
    setIsContracted: (v: boolean) => void;
  },
): DialogConfig {
  const { onClose, onSubmit, onRetry, setName, isContracted, setIsContracted } =
    handlers;

  if (state === "success") {
    return {
      title: "",
      content: (
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Typography variant="header-small">
            <strong>Your request has been submitted</strong>
          </Typography>
          <Typography variant="body-medium">
            Your request for &ldquo;{name}&rdquo; has been submitted. Please
            allow up to 24 hours. Email{" "}
            <Link
              href="mailto:support@centrobenefits.com"
              color="brandPrimary"
              underline="none"
            >
              support@centrobenefits.com
            </Link>{" "}
            with questions.
          </Typography>
        </Box>
      ),
      actions: (
        <Button variant="tertiary" onClick={onClose}>
          Close
        </Button>
      ),
    };
  }

  if (state === "error") {
    return {
      title: "Unable to submit",
      titleColor: "error.main",
      content: (
        <Typography variant="body-medium">
          Failed to submit your request to add &ldquo;{name}&rdquo; as a new
          carrier. Please retry or contact{" "}
          <Link
            href="mailto:support@centrobenefits.com"
            color="brandPrimary"
            underline="none"
          >
            support@centrobenefits.com
          </Link>{" "}
          if the problem continues.
        </Typography>
      ),
      actions: (
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="tertiary" onClick={onRetry}>
            Retry
          </Button>
        </>
      ),
    };
  }

  // idle | loading
  return {
    title: "Request addition of new carrier",
    content: (
      <Box display="flex" flexDirection="column" gap={2} mt={1}>
        <Box>
          <Typography variant="body-medium" mb={0.5}>
            Carrier Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Please double check your spelling."
          />
        </Box>
        <Box>
          <Typography variant="body-medium" mb={0.5}>
            Carrier is contracted with Centro
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isContracted}
                onChange={(e) => setIsContracted(e.target.checked)}
                size="small"
              />
            }
            label="Yes"
          />
        </Box>
        <Alert severity="info">
          Requests are typically reviewed and processed within 24 hours.
        </Alert>
      </Box>
    ),
    actions: (
      <>
        <Button
          variant="tertiary"
          onClick={onClose}
          disabled={state === "loading"}
        >
          Cancel
        </Button>
        <Button
          variant="tertiary"
          onClick={onSubmit}
          disabled={!name.trim() || state === "loading"}
          startIcon={
            state === "loading" ? (
              <CircularProgress size={14} color="inherit" />
            ) : null
          }
        >
          {state === "loading" ? "Submitting..." : "Submit"}
        </Button>
      </>
    ),
  };
}

export function RequestCarrierDialog({
  carrierName,
  onClose,
}: RequestCarrierDialogProps): React.JSX.Element {
  const [name, setName] = useState(carrierName);
  const [isContracted, setIsContracted] = useState(false);
  const { requestCarrier, loading, error, success } = useRequestCarrier();

  useEffect(() => {
    setName(carrierName);
  }, [carrierName]);

  const state: DialogState = loading
    ? "loading"
    : error
      ? "error"
      : success
        ? "success"
        : "idle";

  const { title, titleColor, content, actions } = getDialogConfig(state, name, {
    onClose,
    onSubmit: () => requestCarrier(name, isContracted),
    onRetry: () => requestCarrier(name, isContracted),
    setName,
    isContracted,
    setIsContracted,
  });

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      {title && <DialogTitle color={titleColor}>{title}</DialogTitle>}
      <DialogContent>{content}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}
